import {
    GetLoanDocumentsRequiredCreditFacilityResponse,
    GetLoanDocumentsRequiredFlexi24Response,
    GetLoanDocumentsRequiredIwocapayResponse,
    GetLoanDocumentsRequiredMerchantCashAdvanceResponse,
    GetStateByStateKeyResponse
} from '@iwoca/lapi-client/edge';
import {
    getApplicant,
    getPersonFromUID
} from '../../../../api/lendingAPI/CustomerStateAPIHelper';
import _ from 'lodash';
import { DrawDownAction } from '../../../drawDown/DrawDown.reducer';
import {
    SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT,
    SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT
} from './SignDocuments.actions';

const GUARANTOR_AGREEMENT = 'guarantee';
const MASTER_LOAN_AGREEMENT = 'master_loan_agreement';
const REPEAT_LOAN_AGREEMENT = 'repeat_loan_agreement';
const SECCI = 'secci';
const PERSONAL_GUARANTEE = 'guarantee';
const PRE_CONTRACT = 'reasonable_explanation';

const SIGN_DOCUMENT_PRIORITY = [
    PRE_CONTRACT,
    MASTER_LOAN_AGREEMENT,
    REPEAT_LOAN_AGREEMENT,
    SECCI,
    PERSONAL_GUARANTEE,
    GUARANTOR_AGREEMENT
];

type TRequiredLoanDocuments =
    | GetLoanDocumentsRequiredCreditFacilityResponse['data']
    | GetLoanDocumentsRequiredFlexi24Response['data']
    | GetLoanDocumentsRequiredIwocapayResponse['data']
    | GetLoanDocumentsRequiredMerchantCashAdvanceResponse['data'];

export type TSignDocument = {
    uid: string;
    status: ReturnType<typeof getSignDocumentStatus>;
    type: string;
    title: ReturnType<typeof getDocumentTitle>;
    url: ReturnType<typeof getDownloadDocument>;
    signatureMethod: ReturnType<typeof getSignDocumentSignatureMethod>;
    personToSign: ReturnType<typeof getPersonFromUID>;

    requiresNonApplicantSigning: ReturnType<
        typeof hasNonApplicantSigningRequirement
    >;
};

function prioritySignDocument(document: TSignDocument) {
    const priority = SIGN_DOCUMENT_PRIORITY.indexOf(document.type) || 0;

    if (document.signatureMethod === 'WET') {
        return priority;
    }

    //TODO: cover pending document scenario once there
    if (document.status !== 'COMPLETED') {
        return priority - SIGN_DOCUMENT_PRIORITY.length;
    }

    return priority;
}

export function getSigningDocument(
    loan_documents: TRequiredLoanDocuments,
    uid: TRequiredLoanDocuments['required_loan_documents'][0]['document_id']
) {
    const document = _.chain(loan_documents)
        .get('required_loan_documents', [])
        .find(['document_id', uid])
        .value();

    return {
        uid: document?.document_id,
        unsignedURL: document?.downloads?.unsigned,
        title: document?.title,
        description: document?.description,
        documentType: document?.type
    };
}

export function getSignedDocument(
    loan_documents: TRequiredLoanDocuments,
    uid: TRequiredLoanDocuments['required_loan_documents'][0]['document_id']
) {
    const document = _.chain(loan_documents)
        .get('required_loan_documents', [])
        .find(['document_id', uid])
        .value();

    return {
        uid: document?.document_id,
        signedURL: getSignedURL(document),
        title: document?.title,
        description: document?.description
    };
}

export function getSignedURL(
    document: TRequiredLoanDocuments['required_loan_documents'][0]
) {
    const signedURL = document?.downloads?.signed;

    // This is for documents which are 'agreed' but do not require 'signing'
    if (!signedURL && document.agreed) {
        return document?.downloads?.unsigned;
    }

    return signedURL;
}

export function getSignedDocumentURL(
    action: DrawDownAction
): ReturnType<typeof addPDFQueryToDocumentDownload> | undefined {
    if (action.type === SIGN_DOCUMENT_LOAD_SIGNED_DOCUMENT_CONTENT) {
        const url = action.signedDocument.signedURL;

        return addPDFQueryToDocumentDownload(url);
    }
    return undefined;
}

export function getSigningDocumentURL(
    action: DrawDownAction
): ReturnType<typeof addPDFQueryToDocumentDownload> | undefined {
    if (action.type === SIGN_DOCUMENT_LOAD_DOCUMENT_CONTENT) {
        const url = action?.signingDocument.unsignedURL;
        return addPDFQueryToDocumentDownload(url);
    }
    return undefined;
}

function addPDFQueryToDocumentDownload(url: string) {
    if (typeof url === 'undefined') {
        return undefined;
    }
    const isQuestionMarkPresent = url && url.indexOf('?') !== -1;
    const seperator = () => {
        if (isQuestionMarkPresent) return '&';
        else return '?';
    };
    return `${url}${seperator()}file_format=pdf`;
}

export function deserialiseSignedDocuments(
    customer_state: GetStateByStateKeyResponse['data'],
    loan_documents: TRequiredLoanDocuments
) {
    const deserialisedSignedDocs = _.chain(loan_documents)
        .get('required_loan_documents', [])
        .map((customer_document) => ({
            uid: customer_document.document_id,
            status: getSignDocumentStatus(customer_document),
            type: customer_document.type,
            title: getDocumentTitle(customer_document),
            url: getDownloadDocument(customer_document),
            signatureMethod: getSignDocumentSignatureMethod(customer_document),
            personToSign: getPersonFromUID(
                customer_state,
                customer_document.required_person_to_sign_uid!
            ),
            requiresNonApplicantSigning: hasNonApplicantSigningRequirement(
                customer_state,
                customer_document.required_person_to_sign_uid!
            )
        }))
        .thru((array) => {
            return array.sort((documentA, documentB) => {
                const priorityA = prioritySignDocument(documentA);
                const priorityB = prioritySignDocument(documentB);

                if (
                    priorityA > priorityB ||
                    (priorityA === priorityB && documentA.uid < documentB.uid)
                ) {
                    return 1;
                }

                return -1;
            });
        })
        .value();

    return deserialisedSignedDocs;
}

function hasNonApplicantSigningRequirement(
    customer_state: GetStateByStateKeyResponse['data'],
    requiredPersonToSignUID: string
): boolean {
    const applicant = getApplicant(customer_state);
    return applicant.uid !== requiredPersonToSignUID;
}

function getSignDocumentStatus(
    customer_document: TRequiredLoanDocuments['required_loan_documents'][0]
): 'COMPLETED' | 'REQUIRED' {
    if (customer_document.agreed) {
        return 'COMPLETED';
    }

    return 'REQUIRED';
}

function getSignDocumentSignatureMethod(
    customer_document: TRequiredLoanDocuments['required_loan_documents'][0]
): 'ELECTRONIC' | 'WET' | undefined {
    switch (customer_document.required_method) {
        case 'electronic_signature':
            return 'ELECTRONIC';
        case 'wet_signature':
            return 'WET';
        default:
            return undefined;
    }
}

function getDownloadDocument(
    customer_document: TRequiredLoanDocuments['required_loan_documents'][0]
) {
    if (customer_document.agreed) {
        return customer_document?.downloads?.signed;
    }

    return customer_document?.downloads?.unsigned;
}

export function getDocumentTitle(
    customer_document: TRequiredLoanDocuments['required_loan_documents'][0]
) {
    /* NOTE: This is needed as technically the title field isn't mandatory in the api however every doc SHOULD have one
       This will happen VERY rarely if ever. */

    if (customer_document.title) {
        return customer_document.title;
    } else {
        return 'Document';
    }
}

export function getCustomerDocumentArray(
    UICreditFacilityCustomerDocument: { uid: string }[],
    action: { signDocumentAction: { uid: string } }
) {
    const updatedDocumentStatus = {
        uid: action.signDocumentAction.uid,
        download_datetime: new Date().toISOString()
    };

    if (typeof UICreditFacilityCustomerDocument === 'undefined') {
        return [updatedDocumentStatus];
    }

    const index = _.findIndex(UICreditFacilityCustomerDocument, {
        uid: action.signDocumentAction.uid
    });

    const newArray = [...UICreditFacilityCustomerDocument];

    if (index === -1) {
        newArray.push(updatedDocumentStatus);
    } else {
        newArray.splice(index, 1, updatedDocumentStatus);
    }

    return newArray;
}

export function buildBodyAgreeDocument(uid: string, agreeDate: string) {
    return JSON.stringify({
        data: {
            document_id: uid,
            date_signed: agreeDate
        }
    });
}
